import React, { useState, useRef, useEffect, useContext } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Upload, Link as LinkIcon, Image as ImageIcon, Trash2, Eye, EyeOff, Bold, Italic, List, ListOrdered, Heading1, Heading2, Sparkles, Loader2, X, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { AuthContext } from '../App';

/**
 * RichTextEditor
 *
 * A React-friendly contentEditable wrapper.
 *
 * The critical bug we fix here: previously this component set
 * `dangerouslySetInnerHTML={{ __html: value }}` on every render. Combined
 * with `onInput → onChange → parent re-render → dangerouslySetInnerHTML
 * writes the exact same HTML back into the node`, that made the browser
 * throw the cursor back to position 0 on every keystroke — the "typing
 * feels backwards / one char per click" symptom.
 *
 * Fix: treat the editor as *uncontrolled*. We only push `value` into the
 * DOM when it changes from **outside** (i.e. isn't just the echo of the
 * user's own keystroke). Internal edits update the parent via onChange
 * but do NOT reset the DOM.
 */
export const RichTextEditor = ({ value, onChange, placeholder, aiContext }) => {
 const { API } = useContext(AuthContext);
 const editorRef = useRef(null);
 const fileInputRef = useRef(null);
 const [uploading, setUploading] = useState(false);
 // AI generate / improve modal state — inline so we don't need to pull in
 // a separate portal component. The RichTextEditor is the single hub for
 // rich text across LessonForm (description) AND the multi-content section
 // rich text field, so giving it AI here means every rich text field on the
 // lesson editor gets "Generate with AI" and "Improve" for free.
 const [aiOpen, setAiOpen] = useState(false);
 const [aiPrompt, setAiPrompt] = useState('');
 const [aiMode, setAiMode] = useState('generate'); // 'generate' | 'improve'
 const [aiRunning, setAiRunning] = useState(false);
 // Remember the last value we told the parent about, so we can tell an
 // external change apart from our own echo.
 const lastEmittedRef = useRef(value ?? '');
 // Seed the DOM exactly once on mount (or when the ref first exists).
 const seededRef = useRef(false);

 // Effect: sync from `value` prop → DOM, only when the change originates
 // outside of the editor (e.g. form reset, loaded from server).
 useEffect(() => {
 const el = editorRef.current;
 if (!el) return;
 const incoming = value ?? '';
 if (!seededRef.current) {
 el.innerHTML = incoming;
 lastEmittedRef.current = incoming;
 seededRef.current = true;
 return;
 }
 if (incoming !== lastEmittedRef.current && incoming !== el.innerHTML) {
 // Truly external update — accept it. This preserves external
 // form-reset flows without breaking typing.
 el.innerHTML = incoming;
 lastEmittedRef.current = incoming;
 }
 }, [value]);

 const emit = () => {
 const el = editorRef.current;
 if (!el) return;
 const html = el.innerHTML;
 lastEmittedRef.current = html;
 onChange(html);
 };

 const applyFormat = (command, val = null) => {
 // `document.execCommand` is deprecated but still the pragmatic path
 // for a lightweight contentEditable toolbar; every modern browser
 // still supports it for basic formatting.
 document.execCommand(command, false, val);
 // Re-focus so the caret stays inside the editor after clicking a
 // toolbar button.
 editorRef.current?.focus();
 emit();
 };

 const handleInput = () => emit();

 // Upload a single image blob to object storage and inline-insert it at
 // the current caret. Used by both clipboard-paste and the toolbar picker.
 const uploadImage = async (file) => {
   if (!file) return;
   if (!file.type?.startsWith('image/')) {
     toast.error('Only image files are supported here.');
     return;
   }
   if (file.size > 10 * 1024 * 1024) {
     toast.error('Image is over the 10 MB limit.');
     return;
   }
   const form = new FormData();
   form.append('file', file, file.name || 'pasted-image.png');
   setUploading(true);
   try {
     const res = await axios.post(`${API}/uploads/lms-image`, form);
     const url = res.data?.url;
     if (!url) throw new Error('Upload succeeded but no URL returned');
     // Refocus so the caret lands inside the editor before we insert,
     // otherwise execCommand inserts into <body> and nothing shows up.
     editorRef.current?.focus();
     document.execCommand(
       'insertHTML', false,
       `<img src="${url}" alt="${(file.name || 'image').replace(/"/g, '')}" style="max-width:100%;height:auto;border-radius:6px;margin:4px 0;" />`,
     );
     emit();
     toast.success('Image uploaded');
   } catch (e) {
     const msg = e?.response?.data?.detail || e.message || 'Upload failed';
     toast.error(String(msg));
   } finally {
     setUploading(false);
   }
 };

 // Paste handler:
 //   • Image on clipboard → upload + inline-insert
 //   • text/html on clipboard → sanitised HTML paste (keeps headings,
 //     lists, links, bold — this is how the AI Workpad "Copy formatted
 //     HTML" flow lands as rich content instead of a wall of tags)
 //   • Otherwise → plain-text paste to keep Word/Docs styling out.
 const handlePaste = (e) => {
   const items = e.clipboardData?.items;
   if (items && items.length) {
     for (let i = 0; i < items.length; i++) {
       const it = items[i];
       if (it.kind === 'file' && it.type?.startsWith('image/')) {
         e.preventDefault();
         const blob = it.getAsFile();
         if (blob) uploadImage(blob);
         return;
       }
     }
   }
   const html = e.clipboardData?.getData('text/html');
   if (html && html.trim()) {
     e.preventDefault();
     document.execCommand('insertHTML', false, sanitiseHtml(html));
     return;
   }
   e.preventDefault();
   const text = (e.clipboardData || window.clipboardData).getData('text/plain');
   document.execCommand('insertText', false, text);
 };

 // Minimal sanitiser — drops <script>/<style>, inline event handlers,
 // and Microsoft Office comment wrappers. Not a security boundary; it's
 // a "keep the paste clean" filter. We rely on browser innerHTML parsing
 // + this pass to avoid pulling in an npm dependency.
 const sanitiseHtml = (html) => {
   let out = String(html || '');
   // Strip Office / Google Docs metadata that survives clipboard.
   out = out.replace(/<!--[\s\S]*?-->/g, '');
   out = out.replace(/<meta[^>]*>/gi, '');
   // Drop script/style entirely.
   out = out.replace(/<script[\s\S]*?<\/script>/gi, '');
   out = out.replace(/<style[\s\S]*?<\/style>/gi, '');
   // Drop inline event handlers (onclick=, onload=, etc).
   out = out.replace(/\son[a-z]+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, '');
   // Strip javascript: URLs.
   out = out.replace(/\s(?:href|src)\s*=\s*(?:"javascript:[^"]*"|'javascript:[^']*')/gi, '');
   return out;
 };

 // Fire the AI generation / improve request. Uses the same LMS section
 // endpoint the AI Workpad uses so behaviour stays consistent.
 const runAI = async () => {
   const prompt = (aiPrompt || '').trim();
   if (!prompt) { toast.error('Enter a prompt first.'); return; }
   const current = editorRef.current?.innerHTML || '';
   setAiRunning(true);
   try {
     const { data } = await axios.post(`${API}/admin/ai/section-content`, {
       lesson_id: aiContext?.lessonId || null,
       section_type: 'rich_text',
       prompt,
       // In improve mode, hand the current HTML to the endpoint's
       // `existing_content` field so the model can rewrite/extend rather
       // than restart from scratch.
       existing_content: aiMode === 'improve' ? current : null,
     });
     const html = data?.content?.html || '';
     if (!html) { toast.error('Model returned no HTML.'); return; }
     // Insert or replace based on mode.
     if (aiMode === 'improve') {
       editorRef.current.innerHTML = sanitiseHtml(html);
     } else {
       // "Generate" mode — insert at end so we don't clobber existing prose.
       const el = editorRef.current;
       el.focus();
       // Move caret to end.
       const range = document.createRange();
       range.selectNodeContents(el);
       range.collapse(false);
       const sel = window.getSelection();
       sel.removeAllRanges();
       sel.addRange(range);
       document.execCommand('insertHTML', false, sanitiseHtml(html));
     }
     emit();
     toast.success(aiMode === 'improve' ? 'Section improved.' : 'AI content inserted.');
     setAiOpen(false);
     setAiPrompt('');
   } catch (err) {
     toast.error(err?.response?.data?.detail || 'AI generation failed.');
   } finally {
     setAiRunning(false);
   }
 };

 return (
 <div className="rich-text-editor border border-slate-300 dark:border-slate-600 rounded-lg overflow-hidden">
 {/* Toolbar */}
 <div className="flex items-center gap-1 p-2 bg-slate-100 dark:bg-slate-800 border-b border-slate-300 dark:border-slate-600 flex-wrap">
 <Button
 type="button"
 size="sm"
 variant="ghost"
 onClick={() => applyFormat('formatBlock', '<h1>')}
 title="Heading 1"
 className="h-8 w-8 p-0"
 >
 <Heading1 className="w-4 h-4" />
 </Button>
 <Button
 type="button"
 size="sm"
 variant="ghost"
 onClick={() => applyFormat('formatBlock', '<h2>')}
 title="Heading 2"
 className="h-8 w-8 p-0"
 >
 <Heading2 className="w-4 h-4" />
 </Button>
 <div className="w-px h-6 bg-slate-300 dark:bg-slate-600 mx-1" />
 <Button
 type="button"
 size="sm"
 variant="ghost"
 onClick={() => applyFormat('bold')}
 title="Bold"
 className="h-8 w-8 p-0"
 >
 <Bold className="w-4 h-4" />
 </Button>
 <Button
 type="button"
 size="sm"
 variant="ghost"
 onClick={() => applyFormat('italic')}
 title="Italic"
 className="h-8 w-8 p-0"
 >
 <Italic className="w-4 h-4" />
 </Button>
 <Button
 type="button"
 size="sm"
 variant="ghost"
 onClick={() => applyFormat('underline')}
 title="Underline"
 className="h-8 w-8 p-0"
 >
 <span className="text-sm font-bold underline">U</span>
 </Button>
 <div className="w-px h-6 bg-slate-300 dark:bg-slate-600 mx-1" />
 <Button
 type="button"
 size="sm"
 variant="ghost"
 onClick={() => applyFormat('insertUnorderedList')}
 title="Bullet List"
 className="h-8 w-8 p-0"
 >
 <List className="w-4 h-4" />
 </Button>
 <Button
 type="button"
 size="sm"
 variant="ghost"
 onClick={() => applyFormat('insertOrderedList')}
 title="Numbered List"
 className="h-8 w-8 p-0"
 >
 <ListOrdered className="w-4 h-4" />
 </Button>
 <div className="w-px h-6 bg-slate-300 dark:bg-slate-600 mx-1" />
 <Button
 type="button"
 size="sm"
 variant="ghost"
 onClick={() => {
 const url = prompt('Enter link URL:');
 if (url) applyFormat('createLink', url);
 }}
 title="Insert Link"
 className="h-8 w-8 p-0"
 >
 <LinkIcon className="w-4 h-4" />
 </Button>
 <input
 type="color"
 onChange={(e) => applyFormat('foreColor', e.target.value)}
 title="Text Color"
 className="w-8 h-8 cursor-pointer border-0"
 />
 <div className="w-px h-6 bg-slate-300 dark:bg-slate-600 mx-1" />
 <Button
   type="button"
   size="sm"
   variant="ghost"
   onClick={() => fileInputRef.current?.click()}
   title="Insert image (or paste from clipboard)"
   className="h-8 px-2 gap-1 text-xs"
   disabled={uploading}
   data-testid="rich-text-insert-image-btn"
 >
   <ImageIcon className="w-4 h-4" />
   {uploading ? 'Uploading…' : 'Image'}
 </Button>
 <input
   ref={fileInputRef}
   type="file"
   accept="image/png,image/jpeg,image/gif,image/webp"
   className="hidden"
   data-testid="rich-text-image-file"
   onChange={(e) => {
     const f = e.target.files?.[0];
     e.target.value = '';   // reset so same file can be picked twice
     if (f) uploadImage(f);
   }}
 />
 <div className="w-px h-6 bg-slate-300 dark:bg-slate-600 mx-1" />
 <Button
   type="button"
   size="sm"
   variant="ghost"
   onClick={() => { setAiMode('generate'); setAiOpen(true); }}
   title="Generate content with AI"
   className="h-8 px-2 gap-1 text-xs text-primary hover:text-primary hover:bg-primary/10 font-semibold"
   data-testid="rich-text-ai-generate-btn"
 >
   <Sparkles className="w-4 h-4" />
   AI
 </Button>
 {/* Only show "Improve" once there's actual content to improve. */}
 {(value || '').trim() && (
   <Button
     type="button"
     size="sm"
     variant="ghost"
     onClick={() => { setAiMode('improve'); setAiOpen(true); }}
     title="Rewrite / expand / shorten the current content with AI"
     className="h-8 px-2 gap-1 text-xs text-primary hover:text-primary hover:bg-primary/10 font-semibold"
     data-testid="rich-text-ai-improve-btn"
   >
     <Wand2 className="w-4 h-4" />
     Improve
   </Button>
 )}
 </div>

 {/* Editor Content — NOTE: no dangerouslySetInnerHTML here on purpose.
    The <div> is uncontrolled; the useEffect above seeds it once and
    only re-writes it on genuine external changes. */}
 <div
 ref={editorRef}
 contentEditable
 suppressContentEditableWarning
 onInput={handleInput}
 onPaste={handlePaste}
 className="min-h-[200px] p-4 bg-white dark:bg-slate-900 text-slate-900 dark:text-white prose dark:prose-invert max-w-none focus:outline-none"
 style={{
 wordWrap: 'break-word',
 overflowWrap: 'break-word'
 }}
 data-placeholder={placeholder || ''}
 data-testid="rich-text-editor"
 />

 <style>{`
 [contentEditable][data-placeholder]:empty:before {
 content: attr(data-placeholder);
 color: #9ca3af;
 pointer-events: none;
 display: block;
 }
 `}</style>

 {/* AI prompt modal — inline overlay so no portal or extra component
     is needed. */}
 {aiOpen && (
   <div
     className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/70 backdrop-blur-sm"
     onClick={() => !aiRunning && setAiOpen(false)}
     data-testid="rich-text-ai-modal"
   >
     <div
       className="bg-background border border-border rounded-xl w-full max-w-md shadow-2xl"
       onClick={(e) => e.stopPropagation()}
     >
       <div className="flex items-start justify-between gap-3 p-4 border-b border-border">
         <div className="min-w-0">
           <div className="text-[10px] uppercase tracking-widest font-bold text-primary">AI assist</div>
           <h3 className="text-base font-bold mt-1 flex items-center gap-1.5">
             {aiMode === 'improve' ? <Wand2 className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
             {aiMode === 'improve' ? 'Improve this content' : 'Generate content'}
           </h3>
           <p className="text-[11px] text-muted-foreground mt-0.5">
             {aiMode === 'improve'
               ? 'Tell the model how to rewrite / expand / shorten what is already here.'
               : 'Describe what you want. Result inserts at the end of the editor.'}
           </p>
         </div>
         <button
           type="button"
           onClick={() => !aiRunning && setAiOpen(false)}
           className="text-muted-foreground hover:text-foreground"
           data-testid="rich-text-ai-close"
         >
           <X className="w-4 h-4" />
         </button>
       </div>
       <div className="p-4 space-y-3">
         <textarea
           value={aiPrompt}
           onChange={(e) => setAiPrompt(e.target.value)}
           rows={4}
           placeholder={aiMode === 'improve'
             ? 'e.g. "rewrite in a friendlier tone for beginners" or "expand each bullet into a short paragraph"'
             : 'e.g. "3-paragraph intro explaining sublimation for a home cook, plus a bullet list of 4 safety tips"'}
           className="w-full text-sm p-3 rounded-lg border border-input bg-background"
           data-testid="rich-text-ai-prompt"
           autoFocus
         />
         {/* Only show the mode toggle when there's existing content — an
             empty editor can only Append. */}
         {(value || '').trim() && (
           <div className="inline-flex rounded-full border border-border overflow-hidden text-xs" data-testid="rich-text-ai-mode-toggle">
             <button
               type="button"
               onClick={() => setAiMode('generate')}
               className={`px-3 py-1 font-semibold ${aiMode === 'generate' ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted'}`}
               data-testid="rich-text-ai-mode-generate"
             >
               Append
             </button>
             <button
               type="button"
               onClick={() => setAiMode('improve')}
               className={`px-3 py-1 font-semibold ${aiMode === 'improve' ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted'}`}
               data-testid="rich-text-ai-mode-improve"
             >
               Replace (improve)
             </button>
           </div>
         )}
         <div className="flex gap-2">
           <Button
             type="button"
             variant="outline"
             onClick={() => setAiOpen(false)}
             disabled={aiRunning}
             className="flex-1"
             data-testid="rich-text-ai-cancel"
           >
             Cancel
           </Button>
           <Button
             type="button"
             onClick={runAI}
             disabled={aiRunning || !aiPrompt.trim()}
             className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
             data-testid="rich-text-ai-run"
           >
             {aiRunning
               ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Generating…</>
               : <><Sparkles className="w-4 h-4 mr-1.5" /> {aiMode === 'improve' ? 'Improve' : 'Generate'}</>}
           </Button>
         </div>
       </div>
     </div>
   </div>
 )}
 </div>
 );
};

// Video URL Input with Preview
export const VideoInput = ({ value, onChange }) => {
 const [showPreview, setShowPreview] = useState(false);

 const getVideoId = (url) => {
 if (!url) return null;
 try {
 if (url.includes('youtube.com')) {
 return new URL(url).searchParams.get('v');
 } else if (url.includes('youtu.be')) {
 return url.split('/').pop();
 }
 } catch (e) {
 return null;
 }
 return null;
 };

 const videoId = getVideoId(value);

 return (
 <div className="space-y-3">
 <div>
 <Label>YouTube Video URL</Label>
 <div className="flex gap-2">
 <Input
 value={value || ''}
 onChange={(e) => onChange(e.target.value)}
 placeholder="https://www.youtube.com/watch?v=..."
 className="flex-1"
 />
 {videoId && (
 <Button
 type="button"
 variant="outline"
 onClick={() => setShowPreview(!showPreview)}
 >
 {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
 </Button>
 )}
 </div>
 </div>
 
 {showPreview && videoId && (
 <div className="aspect-video w-full border-2 border-slate-300 dark:border-slate-600 rounded-lg overflow-hidden">
 <iframe
 src={`https://www.youtube.com/embed/${videoId}`}
 className="w-full h-full"
 allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
 allowFullScreen
 title="Video preview"
 />
 </div>
 )}
 </div>
 );
};

// PDF/Slide URL Input with Preview
export const DocumentInput = ({ value, onChange, type = 'pdf' }) => {
 const [showPreview, setShowPreview] = useState(false);

 return (
 <div className="space-y-3">
 <div>
 <Label>{type === 'pdf' ? 'PDF Document URL' : 'Presentation URL (Google Slides, etc.)'}</Label>
 <div className="flex gap-2">
 <Input
 value={value || ''}
 onChange={(e) => onChange(e.target.value)}
 placeholder={type === 'pdf' ? 'https://example.com/document.pdf' : 'https://docs.google.com/presentation/d/...'}
 className="flex-1"
 />
 {value && (
 <Button
 type="button"
 variant="outline"
 onClick={() => setShowPreview(!showPreview)}
 >
 {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
 </Button>
 )}
 </div>
 {type === 'presentation' && (
 <p className="text-xs text-slate-500 mt-1">
 For Google Slides: File → Share → Publish to web → Get embed link
 </p>
 )}
 </div>
 
 {showPreview && value && (
 <div className="aspect-video w-full border-2 border-slate-300 dark:border-slate-600 rounded-lg overflow-hidden">
 <iframe
 src={value}
 className="w-full h-full"
 allowFullScreen
 title="Document preview"
 />
 </div>
 )}
 </div>
 );
};

// Download Manager Component
export const DownloadManager = ({ downloads, onChange }) => {
 const [newDownload, setNewDownload] = useState({ title: '', url: '', description: '', size: '' });
 const [bulkMode, setBulkMode] = useState(false);
 const [bulkUrls, setBulkUrls] = useState('');

 const addDownload = () => {
 if (!newDownload.title || !newDownload.url) return;
 
 onChange([...(downloads || []), { ...newDownload, id: Date.now().toString() }]);
 setNewDownload({ title: '', url: '', description: '', size: '' });
 };

 const addBulkDownloads = () => {
 if (!bulkUrls.trim()) return;
 
 const urls = bulkUrls.split('\n').filter(line => line.trim());
 const newDownloads = urls.map((line, index) => {
 const parts = line.split('|').map(p => p.trim());
 return {
 id: `${Date.now()}-${index}`,
 title: parts[0] || `Download ${downloads?.length + index + 1}`,
 url: parts[1] || parts[0],
 description: parts[2] || '',
 size: parts[3] || ''
 };
 });
 
 onChange([...(downloads || []), ...newDownloads]);
 setBulkUrls('');
 setBulkMode(false);
 };

 const removeDownload = (id) => {
 onChange(downloads.filter(d => d.id !== id));
 };

 return (
 <Card>
 <CardHeader>
 <CardTitle className="text-base">📥 Download Manager</CardTitle>
 </CardHeader>
 <CardContent className="space-y-4">
 {/* Existing Downloads */}
 {downloads && downloads.length > 0 && (
 <div className="space-y-2">
 <Label className="text-sm font-semibold">Available Downloads ({downloads.length})</Label>
 {downloads.map((download) => (
 <div key={download.id} className="flex items-start gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded">
 <div className="flex-1">
 <p className="font-medium">{download.title}</p>
 {download.description && (
 <p className="text-xs text-slate-600 dark:text-neutral-400">{download.description}</p>
 )}
 <p className="text-xs text-slate-500 mt-1 truncate">{download.url}</p>
 {download.size && (
 <span className="text-xs bg-green-200 dark:bg-green-800 px-2 py-1 rounded mt-1 inline-block">
 {download.size}
 </span>
 )}
 </div>
 <Button
 type="button"
 size="sm"
 variant="ghost"
 onClick={() => removeDownload(download.id)}
 className="text-red-600"
 >
 <Trash2 className="w-4 h-4" />
 </Button>
 </div>
 ))}
 </div>
 )}

 {/* Add New Download */}
 <div className="border-t pt-4 space-y-3">
 <div className="flex items-center justify-between">
 <Label className="text-sm font-semibold">Add Downloads</Label>
 <Button
 type="button"
 size="sm"
 variant="ghost"
 onClick={() => setBulkMode(!bulkMode)}
 className="text-xs"
 >
 {bulkMode ? '📝 Single Mode' : '📋 Bulk Add'}
 </Button>
 </div>

 {!bulkMode ? (
 /* Single Add Mode */
 <>
 <div>
 <Label>File Title *</Label>
 <Input
 value={newDownload.title}
 onChange={(e) => setNewDownload({ ...newDownload, title: e.target.value })}
 placeholder="Course Workbook"
 />
 </div>

 <div>
 <Label>File URL *</Label>
 <Input
 value={newDownload.url}
 onChange={(e) => setNewDownload({ ...newDownload, url: e.target.value })}
 placeholder="https://example.com/workbook.pdf"
 />
 </div>

 <div>
 <Label>Description (optional)</Label>
 <Input
 value={newDownload.description}
 onChange={(e) => setNewDownload({ ...newDownload, description: e.target.value })}
 placeholder="Complete exercises and activities"
 />
 </div>

 <div>
 <Label>File Size (optional)</Label>
 <Input
 value={newDownload.size}
 onChange={(e) => setNewDownload({ ...newDownload, size: e.target.value })}
 placeholder="2.5 MB"
 />
 </div>

 <Button
 type="button"
 onClick={addDownload}
 variant="outline"
 className="w-full bg-green-600 hover:bg-green-700 text-white"
 disabled={!newDownload.title || !newDownload.url}
 >
 <Upload className="w-4 h-4 mr-2" />
 Add Download
 </Button>
 </>
 ) : (
 /* Bulk Add Mode */
 <>
 <div>
 <Label>Bulk Add Downloads (one per line)</Label>
 <textarea
 value={bulkUrls}
 onChange={(e) => setBulkUrls(e.target.value)}
 placeholder="Title | URL | Description | Size&#10;Workbook | https://example.com/workbook.pdf | Exercises | 2.5 MB&#10;Guide | https://example.com/guide.pdf&#10;&#10;Or just URLs (one per line):&#10;https://example.com/file1.pdf&#10;https://example.com/file2.pdf"
 className="w-full p-3 border rounded-lg min-h-[150px] font-mono text-sm"
 />
 <p className="text-xs text-slate-500 mt-2">
 💡 Format: <code>Title | URL | Description | Size</code> (pipe-separated) or just URLs
 </p>
 </div>

 <Button
 type="button"
 onClick={addBulkDownloads}
 variant="outline"
 className="w-full bg-green-600 hover:bg-green-700 text-white"
 disabled={!bulkUrls.trim()}
 >
 <Upload className="w-4 h-4 mr-2" />
 Add All Downloads
 </Button>
 </>
 )}
 </div>
 </CardContent>
 </Card>
 );
};

// Image/Diagram Manager Component
export const ImageManager = ({ images, onChange }) => {
 const [newImage, setNewImage] = useState({ url: '', caption: '', alt: '' });
 const [previewImage, setPreviewImage] = useState(null);
 const [bulkMode, setBulkMode] = useState(false);
 const [bulkUrls, setBulkUrls] = useState('');

 const addImage = () => {
 if (!newImage.url) return;
 
 onChange([...(images || []), { ...newImage, id: Date.now().toString() }]);
 setNewImage({ url: '', caption: '', alt: '' });
 setPreviewImage(null);
 };

 const addBulkImages = () => {
 if (!bulkUrls.trim()) return;
 
 const urls = bulkUrls.split('\n').filter(line => line.trim());
 const newImages = urls.map((line, index) => {
 const parts = line.split('|').map(p => p.trim());
 return {
 id: `${Date.now()}-${index}`,
 url: parts[0],
 caption: parts[1] || '',
 alt: parts[2] || `Image ${images?.length + index + 1}`
 };
 });
 
 onChange([...(images || []), ...newImages]);
 setBulkUrls('');
 setBulkMode(false);
 };

 const removeImage = (id) => {
 onChange(images.filter(img => img.id !== id));
 };

 return (
 <Card>
 <CardHeader>
 <CardTitle className="text-base">🖼️ Image & Diagram Manager</CardTitle>
 </CardHeader>
 <CardContent className="space-y-4">
 {/* Existing Images */}
 {images && images.length > 0 && (
 <div className="space-y-2">
 <Label className="text-sm font-semibold">Images ({images.length})</Label>
 <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
 {images.map((image) => (
 <div key={image.id} className="relative group">
 <img
 src={image.url}
 alt={image.alt || 'Image'}
 className="w-full aspect-video object-cover rounded border-2 border-slate-200 dark:border-slate-700"
 onError={(e) => { e.target.src = 'https://via.placeholder.com/300x200?text=Image'; }}
 />
 {image.caption && (
 <p className="text-xs text-slate-600 dark:text-neutral-400 mt-1">{image.caption}</p>
 )}
 <Button
 type="button"
 size="sm"
 variant="ghost"
 onClick={() => removeImage(image.id)}
 className="absolute top-1 right-1 bg-red-600 text-white opacity-0 group-hover:opacity-100 transition-opacity"
 >
 <Trash2 className="w-3 h-3" />
 </Button>
 </div>
 ))}
 </div>
 </div>
 )}

 {/* Add New Image */}
 <div className="border-t pt-4 space-y-3">
 <div className="flex items-center justify-between">
 <Label className="text-sm font-semibold">Add Images</Label>
 <Button
 type="button"
 size="sm"
 variant="ghost"
 onClick={() => {
 setBulkMode(!bulkMode);
 setPreviewImage(null);
 }}
 className="text-xs"
 >
 {bulkMode ? '📝 Single Mode' : '📋 Bulk Add'}
 </Button>
 </div>

 {!bulkMode ? (
 /* Single Add Mode */
 <>
 <div>
 <Label>Image URL *</Label>
 <div className="flex gap-2">
 <Input
 value={newImage.url}
 onChange={(e) => setNewImage({ ...newImage, url: e.target.value })}
 placeholder="https://example.com/image.jpg"
 className="flex-1"
 />
 {newImage.url && (
 <Button
 type="button"
 variant="outline"
 onClick={() => setPreviewImage(previewImage ? null : newImage.url)}
 >
 <Eye className="w-4 h-4" />
 </Button>
 )}
 </div>
 </div>

 {previewImage && (
 <img
 src={previewImage}
 alt="Preview"
 className="w-full max-h-64 object-contain border-2 border-slate-300 dark:border-slate-600 rounded"
 onError={(e) => { e.target.src = 'https://via.placeholder.com/300x200?text=Invalid+Image'; }}
 />
 )}

 <div>
 <Label>Caption (optional)</Label>
 <Input
 value={newImage.caption}
 onChange={(e) => setNewImage({ ...newImage, caption: e.target.value })}
 placeholder="Figure 1: Freeze dryer setup"
 />
 </div>

 <div>
 <Label>Alt Text (optional)</Label>
 <Input
 value={newImage.alt}
 onChange={(e) => setNewImage({ ...newImage, alt: e.target.value })}
 placeholder="Descriptive text for accessibility"
 />
 </div>

 <Button
 type="button"
 onClick={addImage}
 variant="outline"
 className="w-full bg-blue-600 hover:bg-blue-700 text-white"
 disabled={!newImage.url}
 >
 <ImageIcon className="w-4 h-4 mr-2" />
 Add Image
 </Button>
 </>
 ) : (
 /* Bulk Add Mode */
 <>
 <div>
 <Label>Bulk Add Images (one per line)</Label>
 <textarea
 value={bulkUrls}
 onChange={(e) => setBulkUrls(e.target.value)}
 placeholder="URL | Caption | Alt Text&#10;https://example.com/image1.jpg | Figure 1 | Diagram showing process&#10;https://example.com/image2.jpg | Figure 2 | Equipment setup&#10;&#10;Or just URLs (one per line):&#10;https://example.com/photo1.jpg&#10;https://example.com/photo2.jpg&#10;https://example.com/photo3.jpg"
 className="w-full p-3 border rounded-lg min-h-[150px] font-mono text-sm"
 />
 <p className="text-xs text-slate-500 mt-2">
 💡 Format: <code>URL | Caption | Alt Text</code> (pipe-separated) or just URLs
 </p>
 </div>

 <Button
 type="button"
 onClick={addBulkImages}
 variant="outline"
 className="w-full bg-blue-600 hover:bg-blue-700 text-white"
 disabled={!bulkUrls.trim()}
 >
 <ImageIcon className="w-4 h-4 mr-2" />
 Add All Images
 </Button>
 </>
 )}
 </div>
 </CardContent>
 </Card>
 );
};
